import { call, getContext, put, takeEvery, all } from "redux-saga/effects";

import { CONTEXT } from "@const/redux";
import {
  createCommentActionTypes,
  deleteCommentActionTypes,
  editCommentActionTypes,
  getCommentsActionTypes,
  getCommentsPortionActionTypes,
  likeCommentActionTypes,
  unlikeCommentActionTypes,
} from "./actionTypes";
import {
  createCommentFailure,
  createCommentSuccess,
  deleteCommentFailure,
  deleteCommentSuccess,
  editCommentFailure,
  editCommentSuccess,
  getCommentsFailure,
  getCommentsPortionFailure,
  getCommentsPortionSuccess,
  getCommentsSuccess,
  likeCommentFailure,
  likeCommentSuccess,
  unlikeCommentFailure,
  unlikeCommentSuccess,
} from "./actions";
import { normalizeComment, normalizeCommentArray } from "@helpers/comments";
import { setAuthors } from "../authors/actions";
import { setCommentsCount } from "../postDetails/actions";
import { updatePost } from "../posts/actions";

const createCommentAsync = ({ PostsService, id, text }) => {
  if (!PostsService) {
    console.log("Required services does not exist");
    return;
  }
  return PostsService.createComment(id, { text });
};

function* createComment({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);
    const { id, text } = payload;

    const { data } = yield call(createCommentAsync, {
      PostsService,
      id,
      text,
    });
    const { commentsCount, comment: newComment } = data;
    const { result, entities } = normalizeComment(newComment);
    const { authors, comments } = entities;
    const comment = comments[result];

    yield all([
      put(createCommentSuccess({ result, comment })),
      put(setCommentsCount(commentsCount)),
      put(updatePost({ id, post: { commentsCount } })),
      put(setAuthors(authors)),
    ]);
  } catch ({ error }) {
    yield put(createCommentFailure(error));
  }
}

const getCommentsAsync = ({ PostsService, uniqueKey, params }) => {
  if (!PostsService) {
    return;
  }

  return PostsService.getCommentsByUniqueKey(uniqueKey, params);
};

function* getComments({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);
    const { uniqueKey, ...params } = payload;

    const { data, pagination } = yield call(getCommentsAsync, {
      PostsService,
      params,
      uniqueKey,
    });

    const { entities, result: allComments } = normalizeCommentArray(data);
    const { authors, comments: commentById } = entities;
    const { totalCount } = pagination;

    yield all([
      put(setAuthors(authors)),
      put(
        getCommentsSuccess({
          allComments,
          commentById,
          totalCount,
          cursor: allComments.at(-1),
        }),
      ),
    ]);
  } catch ({ error }) {
    yield put(getCommentsFailure(error));
  }
}

function* getPortionComments({ payload }) {
  try {
    const { PostsService } = yield getContext(CONTEXT.SERVICES);
    const { uniqueKey, ...params } = payload;

    const { data, pagination } = yield call(getCommentsAsync, {
      PostsService,
      params,
      uniqueKey,
    });

    const { entities, result: allComments } = normalizeCommentArray(data);
    const { authors, comments: commentById } = entities;
    const { totalCount } = pagination;

    yield all([
      put(setAuthors(authors)),
      put(
        getCommentsPortionSuccess({
          allComments,
          commentById,
          totalCount,
          cursor: allComments.at(-1),
        }),
      ),
    ]);
  } catch ({ error }) {
    yield put(getCommentsPortionFailure(error));
  }
}

const likeCommentAsync = ({ CommentsService, id }) => {
  if (!CommentsService) {
    console.log("Missing required service");
    return;
  }

  return CommentsService.likeComment(id);
};

function* likeComment({ payload }) {
  const { id } = payload;

  try {
    const { CommentsService } = yield getContext(CONTEXT.SERVICES);

    const {
      data: { likesCount },
    } = yield call(likeCommentAsync, { CommentsService, id });

    yield put(likeCommentSuccess({ id, likesCount }));
  } catch ({ error }) {
    yield put(likeCommentFailure({ id }));
  }
}

const unlikeCommentAsync = ({ CommentsService, id }) => {
  if (!CommentsService) {
    console.log("Missing required service");
    return;
  }

  return CommentsService.unlikeComment(id);
};

function* unlikeComment({ payload }) {
  const { id } = payload;

  try {
    const { CommentsService } = yield getContext(CONTEXT.SERVICES);

    const {
      data: { likesCount },
    } = yield call(unlikeCommentAsync, { CommentsService, id });

    yield put(unlikeCommentSuccess({ id, likesCount }));
  } catch ({ error }) {
    yield put(unlikeCommentFailure({ id }));
  }
}

const deleteCommentAsync = ({ CommentsService, id }) => {
  if (!CommentsService) {
    console.log("Missing required service");
    return;
  }

  return CommentsService.deleteComment(id);
};

function* deleteComment({ payload }) {
  const { id } = payload;

  try {
    const { CommentsService } = yield getContext(CONTEXT.SERVICES);

    yield call(deleteCommentAsync, { CommentsService, id });

    yield put(deleteCommentSuccess({ id }));
  } catch ({ error }) {
    yield put(deleteCommentFailure({ id }));
  }
}

const editCommentAsync = ({ CommentsService, id, body }) => {
  if (!CommentsService) {
    console.log("Required service is missing");
    return;
  }

  return CommentsService.editComment(id, body);
};

function* editComment({ payload }) {
  const { id, ...body } = payload;

  try {
    const { CommentsService } = yield getContext(CONTEXT.SERVICES);

    const { data } = yield call(editCommentAsync, {
      CommentsService,
      id,
      body,
    });

    const { result, entities } = normalizeComment(data);
    const { authors, comments } = entities;
    const comment = comments[result];

    yield all([put(editCommentSuccess(comment)), put(setAuthors(authors))]);
  } catch ({ error }) {
    yield put(editCommentFailure({ id }));
  }
}

export function* watchCreateComment() {
  yield takeEvery(createCommentActionTypes.request, createComment);
}

export function* watchGetComments() {
  yield takeEvery(getCommentsActionTypes.request, getComments);
}

export function* watchGetPortionComments() {
  yield takeEvery(getCommentsPortionActionTypes.request, getPortionComments);
}

export function* watchLikeComment() {
  yield takeEvery(likeCommentActionTypes.request, likeComment);
}

export function* watchUnlikeComment() {
  yield takeEvery(unlikeCommentActionTypes.request, unlikeComment);
}

export function* watchDeleteComment() {
  yield takeEvery(deleteCommentActionTypes.request, deleteComment);
}

export function* watchEditComment() {
  yield takeEvery(editCommentActionTypes.request, editComment);
}
